import { z } from 'zod';

/**
 * Semantic-layer contract — request + response shapes used by the
 * "Explore" BI canvas. Mirrors the actual API at
 * `https://semantic.kiwibot.com/docs`, validated end-to-end.
 *
 * Reality of the upstream API (FastAPI wrapping the `mf` CLI):
 *  - `GET /metrics` returns `{ raw: <stdout> }` — a string. The server-
 *    side client parses the bullet list out of it.
 *  - `GET /dimensions?metric=<name>` returns `{ metric, raw }` — also
 *    a string. Dimensions are PER-METRIC, not global.
 *  - `POST /query` is the only fully structured endpoint (rows JSON).
 *
 * Our proxy normalizes this into a friendly catalog shape:
 *  - `metrics: SemanticMetric[]` — `{ name, description? }`.
 *  - `dimensions: SemanticDimension[]` — `{ name, type?, applicableMetrics }`
 *    where `applicableMetrics` is the set of metric names this dim
 *    can be grouped by (computed by fan-out over `/dimensions?metric=…`).
 *
 * Caps mirror operational limits:
 *  - 15 metrics per query: enough for wide tables/pivots; MetricFlow handles it.
 *  - 5 group-by dims: more than that and the chart becomes unreadable.
 *  - 10 where clauses + 5 order_by: generous but bounded.
 *  - 10k row limit: anything bigger should aggregate further.
 */

export const semanticMetricSchema = z.object({
  name: z.string(),
  /** Human display label from the dbt manifest (v2 catalog path only).
   *  Absent on the v1/legacy path and for metrics without a `label:` in YAML. */
  label: z.string().optional(),
  description: z.string().optional(),
});
export type SemanticMetric = z.infer<typeof semanticMetricSchema>;

export const semanticDimensionSchema = z.object({
  name: z.string(),
  description: z.string().optional(),
  type: z.enum(['categorical', 'time']).optional(),
  /** Metric names this dimension can group/filter — empty array means unknown. */
  applicableMetrics: z.array(z.string()).default([]),
  /** Owning semantic model (from the upstream catalog). Lets the Explore
   *  sidebar group dimensions by model. Absent on the v1/legacy path. */
  semanticModel: z.string().nullable().optional(),
});
export type SemanticDimension = z.infer<typeof semanticDimensionSchema>;

export const semanticLayerCatalogSchema = z.object({
  metrics: z.array(semanticMetricSchema),
  dimensions: z.array(semanticDimensionSchema),
  /** ISO timestamp of when this catalog finished its full enrichment.
   *  `null` means we only have the inline-dim slice from `/metrics`; the
   *  enrichment fan-out is still running in the background. Clients can
   *  poll until this becomes non-null to get the complete dim universe. */
  enrichedAt: z.string().datetime().nullable().default(null),
});
export type SemanticLayerCatalog = z.infer<typeof semanticLayerCatalogSchema>;

/**
 * Structured `/v2` + `/catalog` catalog endpoints (semantic-layer-api PR #35).
 * The server-side proxy PREFERS these (real descriptions/types from the dbt
 * manifest, no `mf` subprocess) and falls back to the v1 `mf list` bullet-text
 * endpoints on any failure. Validated with `safeParse` at the proxy border;
 * lenient — unknown keys ignored, nullable text fields tolerated. The web layer
 * never consumes these directly (it gets the normalized `SemanticLayerCatalog`).
 */
export const v2MetricSchema = z.object({
  name: z.string(),
  label: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  type: z.string().optional(),
  meta: z.record(z.string(), z.unknown()).optional(),
});
export const v2MetricsResponseSchema = z.object({
  metrics: z.array(v2MetricSchema),
  count: z.number().int().nonnegative().optional(),
});

export const catalogDimensionSchema = z.object({
  name: z.string(),
  description: z.string().nullable().optional(),
  type: z.string().nullable().optional(),
  semantic_model: z.string().nullable().optional(),
  entity: z.string().nullable().optional(),
});
export const catalogDimensionsResponseSchema = z.object({
  dimensions: z.array(catalogDimensionSchema),
  count: z.number().int().nonnegative().optional(),
});
export const v2MetricDimensionsResponseSchema = z.object({
  metric: z.string(),
  dimensions: z.array(catalogDimensionSchema),
  count: z.number().int().nonnegative().optional(),
});

export const semanticQueryRequestSchema = z.object({
  metrics: z.array(z.string().min(1)).min(1).max(15),
  group_by: z.array(z.string().min(1)).max(5).optional(),
  where: z.array(z.string().min(1)).max(10).optional(),
  order_by: z.array(z.string().min(1)).max(5).optional(),
  limit: z.number().int().positive().max(10_000).default(1_000),
});
export type SemanticQueryRequest = z.infer<typeof semanticQueryRequestSchema>;

/** Row values can be string, number, boolean, or null — Recharts handles
 *  the dispatch on consumption. */
export const semanticQueryRowSchema = z.record(
  z.string(),
  z.union([z.string(), z.number(), z.boolean(), z.null()]),
);
export type SemanticQueryRow = z.infer<typeof semanticQueryRowSchema>;

export const semanticQueryResponseSchema = z.object({
  rows: z.array(semanticQueryRowSchema),
  count: z.number().int().nonnegative(),
  /** Echo of the SQL the semantic API generated (nullable in upstream). */
  sql: z.string().nullable().optional(),
});
export type SemanticQueryResponse = z.infer<typeof semanticQueryResponseSchema>;

/**
 * Distinct values of one dimension — powers the real-value filter picker
 * (checkbox list) and linked/cascade filters. Runs a `group_by:[dimension]`
 * query under the hood (which MetricFlow requires a metric context for); the
 * optional `where` carries the parent filter's selection for cascades.
 */
export const distinctValuesRequestSchema = z.object({
  dimension: z.string().min(1).max(120),
  /** A metric the dimension is groupable by (its `applicableMetrics[0]`). */
  metric: z.string().min(1).max(120),
  /** Cascade: parent filter where-clauses to narrow the offered values. */
  where: z.array(z.string().min(1).max(280)).max(10).default([]),
  limit: z.number().int().positive().max(200).default(100),
});
export type DistinctValuesRequest = z.infer<typeof distinctValuesRequestSchema>;

export const distinctValuesResponseSchema = z.object({
  values: z.array(z.string()),
});
export type DistinctValuesResponse = z.infer<typeof distinctValuesResponseSchema>;

/**
 * Batch query — a dashboard fires one POST for all its cells instead of N
 * (browser connection limits serialize parallel POSTs at ~6-8). Results are
 * positional; each can independently fail without sinking the batch.
 */
export const semanticQueryRunBatchRequestSchema = z.object({
  queries: z.array(semanticQueryRequestSchema).min(1).max(30),
});
export type SemanticQueryRunBatchRequest = z.infer<typeof semanticQueryRunBatchRequestSchema>;

export const semanticQueryBatchErrorSchema = z.object({
  /** Error code, e.g. 'semantic_query_invalid' — same codes as /run. */
  error: z.string(),
  message: z.string(),
  status: z.number().int(),
  bodyExcerpt: z.string().optional(),
});
export type SemanticQueryBatchError = z.infer<typeof semanticQueryBatchErrorSchema>;

export const semanticQueryBatchResultSchema = z.union([
  semanticQueryResponseSchema.extend({ ok: z.literal(true) }),
  z.object({ ok: z.literal(false), error: semanticQueryBatchErrorSchema }),
]);
export type SemanticQueryBatchResult = z.infer<typeof semanticQueryBatchResultSchema>;

export const semanticQueryRunBatchResponseSchema = z.object({
  results: z.array(semanticQueryBatchResultSchema).max(30),
});
export type SemanticQueryRunBatchResponse = z.infer<typeof semanticQueryRunBatchResponseSchema>;
